import { RefreshCw01 } from '@untitledui/icons';
import { useMemo } from 'react';

import type { ActiveCampaignFieldMapping, AhoyPropertyOption } from '../types';
import {
  EditableMappingRow,
  MAPPING_ROW_GRID,
  type MappingUpdate,
} from './EditableMappingRow';

interface MappingSectionsProps {
  mappings: ActiveCampaignFieldMapping[];
  availableAhoyProperties: AhoyPropertyOption[];
  /** Ids of rows with an in-flight save (each tracked independently). */
  savingIds: ReadonlySet<number>;
  /** Friendly error message per failed row, keyed by mappingId. */
  errorsById: ReadonlyMap<number, string>;
  onUpdate: (mappingId: number, body: MappingUpdate) => void;
}

const COLUMN_HEADER_LABEL_CLASS =
  'text-xs font-medium uppercase tracking-wide text-[var(--color-text-tertiary)]';
const SECTION_HEADING_CLASS =
  'text-sm font-semibold text-[var(--color-text-primary)]';
const CAPTION_CLASS = 'text-body-small text-[var(--color-text-secondary)]';

function isSynced(m: ActiveCampaignFieldMapping): boolean {
  return !!m.ahoy_property;
}

function MappingTable({
  mappings,
  availableAhoyProperties,
  savingIds,
  errorsById,
  onUpdate,
}: Pick<
  MappingSectionsProps,
  | 'mappings'
  | 'availableAhoyProperties'
  | 'savingIds'
  | 'errorsById'
  | 'onUpdate'
>) {
  return (
    <div className='overflow-hidden rounded-xl border border-[var(--color-border-secondary)]'>
      <div
        className={`hidden bg-[var(--color-bg-secondary)] px-4 py-2.5 sm:block ${MAPPING_ROW_GRID}`}
      >
        <span className={COLUMN_HEADER_LABEL_CLASS}>ActiveCampaign field</span>
        <span className={COLUMN_HEADER_LABEL_CLASS}>Ahoy property</span>
        <span className={COLUMN_HEADER_LABEL_CLASS}>Direction</span>
        <span className={COLUMN_HEADER_LABEL_CLASS}>On conflict</span>
      </div>
      <div className='divide-y divide-[var(--color-border-secondary)]'>
        {mappings.map(mapping => (
          <EditableMappingRow
            key={mapping.id}
            mapping={mapping}
            availableAhoyProperties={availableAhoyProperties}
            isSaving={savingIds.has(mapping.id)}
            onUpdate={onUpdate}
            error={errorsById.get(mapping.id)}
          />
        ))}
      </div>
    </div>
  );
}

export function MappingSections({
  mappings,
  availableAhoyProperties,
  savingIds,
  errorsById,
  onUpdate,
}: MappingSectionsProps) {
  const synced = useMemo(() => mappings.filter(isSynced), [mappings]);
  const unmappedAcFields = useMemo(
    () => mappings.filter(m => !isSynced(m)),
    [mappings]
  );

  return (
    <div className='space-y-8'>
      {synced.length > 0 && (
        <section className='space-y-2'>
          <h3 className={SECTION_HEADING_CLASS}>Synced fields</h3>
          <MappingTable
            mappings={synced}
            availableAhoyProperties={availableAhoyProperties}
            savingIds={savingIds}
            errorsById={errorsById}
            onUpdate={onUpdate}
          />
        </section>
      )}

      <section className='space-y-4'>
        <h3 className={SECTION_HEADING_CLASS}>Other fields</h3>

        {/* Subscription status is an ActiveCampaign contact attribute, not a
            custom field — it's synced automatically and isn't user-mappable. */}
        <div className='flex items-center justify-between gap-3 rounded-xl border border-[var(--color-border-secondary)] px-4 py-3'>
          <div className='flex items-center gap-2.5'>
            <RefreshCw01
              className='h-4 w-4 shrink-0 text-[var(--color-text-tertiary)]'
              aria-hidden='true'
            />
            <span className='text-sm font-medium text-[var(--color-text-primary)]'>
              Subscription status
            </span>
            <span className='text-xs text-[var(--color-text-tertiary)]'>
              → ActiveCampaign status
            </span>
          </div>
          <span className='text-body-small text-[var(--color-text-tertiary)]'>
            Synced automatically
          </span>
        </div>

        {unmappedAcFields.length > 0 && (
          <div className='space-y-2'>
            <p className={CAPTION_CLASS}>
              ActiveCampaign fields you haven’t mapped yet — map to an existing
              Ahoy property or create one.
            </p>
            <MappingTable
              mappings={unmappedAcFields}
              availableAhoyProperties={availableAhoyProperties}
              savingIds={savingIds}
              errorsById={errorsById}
              onUpdate={onUpdate}
            />
          </div>
        )}
      </section>
    </div>
  );
}
